import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  ArrowRight,
  CheckCircle,
  Globe,
  Palette,
  TrendingUp,
  Users,
  Search,
  Mail,
  Megaphone,
  Package,
  Calendar,
  Sparkles
} from 'lucide-react';
import PublicHeader from './components/PublicHeader';
import PublicFooter from './components/PublicFooter';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ServicesPage = () => {
  const [content, setContent] = useState(null);

  const iconMap = {
    'globe': Globe,
    'palette': Palette,
    'trending-up': TrendingUp,
    'users': Users,
    'search': Search,
    'megaphone': Megaphone,
    'package': Package,
    'calendar': Calendar
  };

  // Gray shaded style for all icons - consistent muted look
  const iconStyle = {
    backgroundColor: 'var(--color-section-alt-bg, #F1F5F9)',
    color: 'var(--color-body-text, #64748B)'
  };

  // Default content
  const defaults = {
    badge_text: 'What We Do',
    page_title: 'Services That Drive Results',
    page_subtitle: 'Comprehensive marketing services backed by 40+ years of experience. From brand strategy to digital execution, we help businesses elevate their marketing and achieve measurable growth.',
    services_list: [
      {
        icon: 'palette',
        title: 'Brand Strategy & Identity',
        description: 'Strategic brand positioning and visual identity systems that differentiate you from competitors and resonate with your target audience.',
        features: ['Brand positioning and messaging', 'Visual identity systems', 'Brand guidelines and standards', 'Brand architecture', 'Naming and tagline development']
      },
      {
        icon: 'globe',
        title: 'Web Design & Development',
        description: 'Custom websites and web applications built to convert visitors into customers with modern, responsive design and SEO best practices.',
        features: ['Custom website design', 'Web application development', 'E-commerce solutions', 'CMS implementation', 'Website optimization & maintenance']
      },
      {
        icon: 'trending-up',
        title: 'Digital Marketing',
        description: 'Full-funnel digital marketing programs that drive measurable results, from awareness through conversion and retention.',
        features: ['Digital strategy development', 'Campaign management', 'Marketing automation', 'Analytics and reporting', 'Conversion optimization']
      },
      {
        icon: 'search',
        title: 'SEO & Paid Search',
        description: 'Drive qualified traffic to your website through organic search optimization and strategic paid search campaigns.',
        features: ['Technical SEO audits', 'On-page optimization', 'Content strategy for SEO', 'Google Ads management', 'Local SEO']
      },
      {
        icon: 'users',
        title: 'Content Marketing',
        description: 'Compelling content that builds authority, engages your audience, and supports your business objectives across all channels.',
        features: ['Content strategy', 'Blog and article writing', 'Case studies and white papers', 'Email newsletter content', 'Social media content']
      },
      {
        icon: 'megaphone',
        title: 'Advertising',
        description: 'Integrated advertising campaigns across consumer and trade channels that build awareness and drive action.',
        features: ['Campaign strategy and planning', 'Creative development', 'Media planning and buying', 'Trade advertising', 'Co-op program management']
      },
      {
        icon: 'package',
        title: 'Packaging & Merchandising',
        description: 'Packaging design and merchandising systems that command shelf presence and communicate your brand effectively.',
        features: ['Packaging design', 'Point-of-sale displays', 'Retail merchandising systems', 'Product catalog design', 'Trade show materials']
      },
      {
        icon: 'calendar',
        title: 'Event Marketing',
        description: 'Strategic event planning and trade show presence that creates memorable brand experiences and generates leads.',
        features: ['Trade show strategy', 'Booth design and production', 'Event marketing campaigns', 'Lead capture and follow-up', 'Post-event analysis']
      }
    ],
    process_steps: [
      { number: '01', title: 'Discovery', description: 'We start by understanding your business, goals, competitive landscape, and target audience.' },
      { number: '02', title: 'Strategy', description: 'We develop a tailored strategy aligned with your objectives and budget.' },
      { number: '03', title: 'Execution', description: 'We bring the strategy to life with precision, creativity, and attention to detail.' },
      { number: '04', title: 'Optimization', description: 'We measure results, learn from data, and continuously improve performance.' }
    ],
    industries: [
      { name: 'Home Improvement & Building Products' },
      { name: 'Manufacturing & Industrial' },
      { name: 'Professional Services' },
      { name: 'B2B & Trade Marketing' },
      { name: 'Consumer Products' }
    ]
  };

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/public/site-content/services`);
        if (response.data && Object.keys(response.data).length > 0) {
          setContent(response.data);
        }
      } catch (err) {
        console.log('Using default content');
      }
    };
    fetchContent();
  }, []);

  // Merge CMS content with defaults
  const c = { ...defaults, ...content };
  
  // Parse services - handle both string features and array features
  const services = (content?.services_list && content.services_list.length > 0) 
    ? content.services_list.map(svc => ({
        ...svc,
        features: typeof svc.features === 'string' 
          ? svc.features.split('\n').filter(f => f.trim())
          : svc.features || []
      }))
    : defaults.services_list;

  const processSteps = (content?.process_steps && content.process_steps.length > 0) 
    ? content.process_steps 
    : defaults.process_steps;

  const industries = (content?.industries && content.industries.length > 0) 
    ? content.industries 
    : defaults.industries;

  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />

      {/* Hero Section */}
      <section 
        className="pt-40 pb-20 lg:pt-48 lg:pb-24"
        style={{ background: `linear-gradient(to bottom right, var(--color-hero-bg-from, #F8FAFC), white, var(--color-hero-bg-to, #EFF6FF))` }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6"
              style={{ backgroundColor: 'var(--color-primary, #1F3C4F)1a', color: 'var(--color-primary, #1F3C4F)' }}
            >
              <Sparkles className="w-4 h-4" />
              {c.badge_text}
            </div>
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6"
              style={{ color: 'var(--color-heading-text, #0F172A)' }}
            >
              {c.page_title}
            </h1>
            <p 
              className="text-xl leading-relaxed"
              style={{ color: 'var(--color-body-text, #475569)' }}
            >
              {c.page_subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, idx) => {
              const IconComponent = iconMap[service.icon] || Globe;
              
              return (
                <Card 
                  key={idx} 
                  className="border-0 shadow-lg hover:shadow-xl transition-shadow group" 
                  data-testid={`service-card-${idx}`}
                  style={{ backgroundColor: 'var(--color-card-bg, #FFFFFF)' }}
                >
                  <CardContent className="p-8">
                    <div 
                      className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform"
                      style={iconStyle}
                    >
                      <IconComponent className="w-8 h-8" />
                    </div>
                    <h3 
                      className="text-2xl font-bold mb-4"
                      style={{ color: 'var(--color-heading-text, #0F172A)' }}
                    >{service.title}</h3>
                    <p 
                      className="mb-6"
                      style={{ color: 'var(--color-body-text, #475569)' }}
                    >{service.description}</p>
                    <ul className="space-y-3">
                      {service.features.map((feature, fIdx) => (
                        <li key={fIdx} className="flex items-start gap-3">
                          <CheckCircle 
                            className="w-5 h-5 flex-shrink-0 mt-0.5" 
                            style={{ color: 'var(--color-primary, #1F3C4F)' }}
                          />
                          <span 
                            className="text-sm"
                            style={{ color: 'var(--color-body-text, #475569)' }}
                          >{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section 
        className="py-20 lg:py-28"
        style={{ backgroundColor: 'var(--color-section-alt-bg, #F8FAFC)' }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ color: 'var(--color-heading-text, #0F172A)' }}
            >
              Our Process
            </h2>
            <p 
              className="text-xl max-w-2xl mx-auto"
              style={{ color: 'var(--color-body-text, #475569)' }}
            >
              A proven approach that delivers results, refined over four decades of experience
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {processSteps.map((step, idx) => (
              <div key={idx} className="relative">
                <div 
                  className="text-6xl font-bold mb-4"
                  style={{ color: 'var(--color-primary, #1F3C4F)1a' }}
                >{step.number}</div>
                <h3 
                  className="text-xl font-bold mb-3"
                  style={{ color: 'var(--color-heading-text, #0F172A)' }}
                >{step.title}</h3>
                <p style={{ color: 'var(--color-body-text, #475569)' }}>{step.description}</p>
                {idx < processSteps.length - 1 && (
                  <div 
                    className="hidden md:block absolute top-8 right-0 w-1/2 h-0.5"
                    style={{ backgroundColor: 'var(--color-primary, #1F3C4F)33' }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 
                className="text-3xl md:text-4xl font-bold mb-6"
                style={{ color: 'var(--color-heading-text, #0F172A)' }}
              >
                Why Work With SPT Digital Marketing?
              </h2>
              <p 
                className="text-lg mb-8"
                style={{ color: 'var(--color-body-text, #475569)' }}
              >
                We combine enterprise-level marketing expertise with the personal attention 
                of a boutique agency. Here's what sets us apart:
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-4">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: 'var(--color-primary, #1F3C4F)1a', color: 'var(--color-primary, #1F3C4F)' }}
                  >
                    <CheckCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold" style={{ color: 'var(--color-heading-text, #0F172A)' }}>40+ Years of Experience</h3>
                    <p className="text-sm" style={{ color: 'var(--color-body-text, #475569)' }}>Proven strategies refined across decades of real-world results</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: 'var(--color-primary, #1F3C4F)1a', color: 'var(--color-primary, #1F3C4F)' }}
                  >
                    <CheckCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold" style={{ color: 'var(--color-heading-text, #0F172A)' }}>Enterprise Background</h3>
                    <p className="text-sm" style={{ color: 'var(--color-body-text, #475569)' }}>Systems and processes developed managing multi-million dollar programs</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: 'var(--color-primary, #1F3C4F)1a', color: 'var(--color-primary, #1F3C4F)' }}
                  >
                    <CheckCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold" style={{ color: 'var(--color-heading-text, #0F172A)' }}>Personal Approach</h3>
                    <p className="text-sm" style={{ color: 'var(--color-body-text, #475569)' }}>Direct access to senior leadership, not handed off to junior staff</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: 'var(--color-primary, #1F3C4F)1a', color: 'var(--color-primary, #1F3C4F)' }}
                  >
                    <CheckCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold" style={{ color: 'var(--color-heading-text, #0F172A)' }}>Results-Focused</h3>
                    <p className="text-sm" style={{ color: 'var(--color-body-text, #475569)' }}>We measure success by your business outcomes, not vanity metrics</p>
                  </div>
                </li>
              </ul>
            </div>
            <div 
              className="rounded-3xl p-10 text-white"
              style={{ backgroundColor: 'var(--color-button-bg, var(--color-primary, #1F3C4F))' }}
            >
              <h3 className="text-2xl font-bold mb-6">Industries We Serve</h3>
              <p className="text-white/80 mb-8">
                While we work with businesses across sectors, we have deep expertise in:
              </p>
              <ul className="space-y-3 mb-8">
                {industries.map((industry, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-white/80" />
                    <span>{industry.name}</span>
                  </li>
                ))}
              </ul>
              <Link to="/portfolio">
                <Button 
                  size="lg" 
                  className="w-full"
                  style={{ 
                    backgroundColor: 'var(--color-button-secondary-bg, #FFFFFF)',
                    color: 'var(--color-primary, #1F3C4F)',
                    borderRadius: 'var(--btn-computed-radius, 0.5rem)'
                  }}
                >
                  View Our Work
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-20 lg:py-28"
        style={{ backgroundColor: 'var(--color-button-bg, var(--color-primary, #1F3C4F))' }}
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Let's discuss how we can help you achieve your marketing goals with the right 
            combination of strategy, creativity, and execution.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button 
                size="lg" 
                className="text-lg px-8 py-6"
                style={{ 
                  backgroundColor: 'var(--color-button-secondary-bg, #FFFFFF)',
                  color: 'var(--color-primary, #1F3C4F)',
                  borderRadius: 'var(--btn-computed-radius, 0.5rem)'
                }}
              >
                <Mail className="w-5 h-5 mr-2" />
                Start a Conversation
              </Button>
            </Link>
            <Link to="/about">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white/10 text-lg px-8 py-6"
                style={{ borderRadius: 'var(--btn-computed-radius, 0.5rem)' }}
              >
                Learn More About Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default ServicesPage;
